/**
 * Offline-first walkthrough completion sync with server preferences.
 * @module features/walkthrough/walkthroughSync
 */
import { fetchWalkthroughPreferences, patchWalkthroughPreferences } from './walkthroughApi';
import { hasCompletedFirstRunWalkthrough } from './walkthroughOrchestrator';
import { WALKTHROUGH_STORAGE_VERSION } from './walkthroughTypes';

/**
 * @param {import('./walkthroughTypes').WalkthroughPersistedState} state
 * @returns {number}
 */
export function getLocalEffectiveCompletionTime(state) {
  if (typeof state.dismissedAt === 'number' && Number.isFinite(state.dismissedAt)) {
    return state.dismissedAt;
  }

  if (Object.keys(state.completedTours).length > 0) {
    return 1;
  }

  return 0;
}

/**
 * @param {import('./walkthroughTypes').WalkthroughPersistedState} local
 * @param {{ walkthroughCompletedVersion?: string | null, walkthroughCompletedAt?: string | null } | null | undefined} serverPrefs
 * @returns {import('./walkthroughTypes').WalkthroughPersistedState}
 */
export function mergeWalkthroughWithServer(local, serverPrefs) {
  if (!serverPrefs?.walkthroughCompletedAt) {
    return local;
  }

  const serverAt = new Date(serverPrefs.walkthroughCompletedAt).getTime();
  if (Number.isNaN(serverAt)) {
    return local;
  }

  const localEffectiveAt = getLocalEffectiveCompletionTime(local);

  if (serverAt <= localEffectiveAt) {
    return local;
  }

  return {
    ...local,
    dismissedAt: local.dismissedAt ? Math.max(local.dismissedAt, serverAt) : serverAt,
  };
}

/**
 * @param {import('./walkthroughTypes').WalkthroughPersistedState} persistedState
 * @param {object | null | undefined} user
 * @returns {Promise<void>}
 */
export async function pushWalkthroughCompletionIfEligible(persistedState, user, syncEnabled = false) {
  if (!syncEnabled || !user) {
    return;
  }

  if (!hasCompletedFirstRunWalkthrough(user, persistedState)) {
    return;
  }

  const completedAtMs = persistedState.dismissedAt ?? Date.now();

  await patchWalkthroughPreferences({
    walkthroughCompletedVersion: WALKTHROUGH_STORAGE_VERSION,
    walkthroughCompletedAt: new Date(completedAtMs).toISOString(),
  });
}

/**
 * Pull server preferences and merge with local state. Local wins when newer.
 * @param {import('./walkthroughTypes').WalkthroughPersistedState} local
 * @param {object | null | undefined} user
 * @returns {Promise<import('./walkthroughTypes').WalkthroughPersistedState>}
 */
export async function pullAndMergeWalkthroughPreferences(local, user, syncEnabled = false) {
  if (!syncEnabled || !user) {
    return local;
  }

  try {
    const response = await fetchWalkthroughPreferences();
    const serverPrefs = response?.data ?? null;
    const merged = mergeWalkthroughWithServer(local, serverPrefs);

    const localEffectiveAt = getLocalEffectiveCompletionTime(local);
    const serverAt = serverPrefs?.walkthroughCompletedAt
      ? new Date(serverPrefs.walkthroughCompletedAt).getTime()
      : 0;

    if (localEffectiveAt > serverAt && hasCompletedFirstRunWalkthrough(user, merged)) {
      pushWalkthroughCompletionIfEligible(merged, user, syncEnabled).catch(() => {});
    }

    return merged;
  } catch {
    return local;
  }
}
